
import { create } from 'zustand';
import AppService from '../services/AppService';
import { IVATTypes, VATStore } from '../typings';
import NewCustomerForm from './NewCustomerForm';
import * as StoreHelper from './StoreHelper';
import { validateEndUserDetails, validateRelationshipWithServiceUser, validateVATForm } from './Validations';

const appsService = new AppService((window as any).shop_domain);

let initialStep: IVATTypes = 'vat_information';
const params = new URLSearchParams(window.location.search);
const orderId: string = params.get('order_id') ?? '';
const secureHash: string = params.get('secure_hash') ?? '';

export const useVATStore = create<VATStore>((set, get) => ({
    steps: ['vat_information', 'gcl_information', 'final'],
    currentStep: initialStep,
    titleOptions: ['Mr', 'Ms', 'Mrs', 'Miss', 'Dr', 'Pro', 'Rev'],
    newCustomerForm: NewCustomerForm.getNewCustomerForm(),
    loading: false,
    vatModal: false,
    gclModal: false,
    error: '',
    orderId: orderId,
    secureHash: secureHash,
    vatDetailsErrors: [],
    vatFormErrors: [],
    signErrors: [],
    sortCode: '',
    accountNumber: '',
    gclErrors: [],
    gclFlowCompleted: false,
    flow: '',
    flowId: '',
    billingInfo: { address: { city: '', country: '', county: '', line1: '', line2: '', zip: '' }, email: '', first_name: '', last_name: '', phone: '' },
    showVATModal: (show: boolean) => {
        set(() => ({ vatModal: show }));
    },
    showGCLModal: (show: boolean) => {
        set(() => ({ gclModal: show }));
    },
    init: async () => {
        if (get().orderId && get().secureHash) {
            try {
                const vatFormDetails = await appsService.getNewCustomerFromByOrderId(parseInt(get().orderId), get().secureHash);
                if (!vatFormDetails.gcl_done) {
                    set(() => ({ newCustomerForm: vatFormDetails.ncf, billingInfo: vatFormDetails.billing_info }));
                } else {
                    set(() => ({ currentStep: 'final' }));
                }
            } catch (e) {
                set(() => ({ error: 'URL is not completed or corrupted!' }));
            }
        } else {
            set(() => ({ error: 'URL is not completed or corrupted!' }));
        }
    },
    setFormValue: (entry: string, fields: (string | number)[], newValue: string | boolean | object) => {
        if (!fields?.length) {
            (get().newCustomerForm as any)[entry] = newValue as any;
        } else if (fields.length === 1) {
            (get().newCustomerForm as any)[entry][fields[0]] = newValue;
        } else if (fields.length === 2) {
            if (typeof fields[1] === 'number') {
                if ((get().newCustomerForm as any)[entry][fields[0]][fields[1]]) {
                    (get().newCustomerForm as any)[entry][fields[0]][fields[1]] = newValue;
                } else {
                    (get().newCustomerForm as any)[entry][fields[0]].push(newValue);
                }
            } else {
                (get().newCustomerForm as any)[entry][fields[0]][fields[1]] = newValue;
            }
        } else if (fields.length === 3) {
            (get().newCustomerForm as any)[entry][fields[0]][fields[1]][fields[2]] = newValue;
        }
    },
    nextStep: () => {
        set(() => ({ loading: true }));
        if (get().currentStep === 'vat_information') {
            let vatFormErrors: string[] = [];
            vatFormErrors = validateVATForm(get().newCustomerForm);
            vatFormErrors.push(...validateEndUserDetails(get().newCustomerForm));
            vatFormErrors.push(...validateRelationshipWithServiceUser(get().newCustomerForm));
            if (vatFormErrors?.length === 0) {
                appsService.saveNewCustomerFormByOrderId(parseInt(get().orderId), get().secureHash ?? '', get().newCustomerForm).then(() => {
                    set(() => ({
                        loading: false,
                        vatFormErrors: vatFormErrors,
                        currentStep: StoreHelper.getNextVatFormStep(get().currentStep, get().steps), error: ''
                    }));
                    setTimeout(() => { try { document.getElementsByClassName('reg-content')[0].scrollTop = 0; } catch (e) { } }, 1);
                }).catch(() => {
                    set(() => ({ loading: false, error: 'Something went wrong!. Try again in a few minutes or contact us at info@careline365.co.uk' }));
                });
            } else {
                set(() => ({ loading: false, vatFormErrors: vatFormErrors, error: 'You need to fill all required fields!' }));
            }
        }
        if (get().currentStep === 'gcl_information') {
            const orderId = get().orderId;
            if (!get().flow && orderId) {
                appsService.getBillingFlowFromVAT({
                    orderId: parseInt(orderId),
                    account: get().accountNumber, sortcode: get().sortCode
                }, get().secureHash ?? '').then((response) => {
                    if (response.url) {
                        setTimeout(() => { window.location.href = response.url; }, 1);
                    } else if (response.msg) {
                        set(() => ({ loading: false, error: 'We have not been able to verify your bank account. Make sure the bank account details are correct or contact us at info@careline365.co.uk' }));
                    } else {
                        set(() => ({ loading: false, error: 'Something went wrong!. Try again in a few minutes or contact us at info@careline365.co.uk' }));
                    }
                }).catch(() => {
                    set(() => ({ loading: false, error: 'Something went wrong!. Try again in a few minutes or contact us at info@careline365.co.uk' }));
                });
            } else {
                set(() => ({ loading: false }));
            }
        }

    },
    previousStep: () => {
        const previousStep = StoreHelper.getNextVatFormStep(get().currentStep, get().steps);
        set((state) => ({ currentStep: previousStep }));
    },
    setAccountNumber: (accountNumber: string) => {
        set((state) => ({ accountNumber: accountNumber }));
    },
    setSortCode: (sortCode: string) => {
        set((state) => ({ sortCode: sortCode }));
    },
    clearError: () => {
        set(() => ({ error: '' }));
    }
}));